import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useSaveWorkingDays } from '../../mutations';
import { useWorkingDays } from '../../queries';
import { WEEKDAYS, type Weekday } from '../../types';
import { errorMessage } from '@/shared/api/problem';
import { Button } from '@/shared/ui/atoms/Button';
import { Checkbox } from '@/shared/ui/atoms/Checkbox';
import { FontIcon } from '@/shared/ui/atoms/FontIcon';
import { Spinner } from '@/shared/ui/atoms/Spinner';
import { useToast } from '@/shared/ui/organisms/Toast/useToast';
import '../RolesManager/RolesManager.styl';

/**
 * The organisation's working days. A member's weekly hours are split among them (20 h and Monday to Friday =
 * 4 h a day), which is what the capacity check assigns to each day of a task's range.
 */
export function WorkingDaysForm({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'workingDays' });
  const days = useWorkingDays();
  if (!days.data) return <Spinner label={t('loading')} />;
  return <WorkingDaysEditor initial={days.data} canEdit={canEdit} />;
}

function WorkingDaysEditor({ initial, canEdit }: { initial: Weekday[]; canEdit: boolean }) {
  const { t } = useTranslation(undefined, { keyPrefix: 'workingDays' });
  const save = useSaveWorkingDays();
  const toast = useToast();
  const [selected, setSelected] = useState<Set<Weekday>>(() => new Set(initial));

  const toggle = (d: Weekday, on: boolean) => {
    const next = new Set(selected);
    if (on) next.add(d);
    else next.delete(d);
    setSelected(next);
  };

  return (
    <div className="f-roles">
      <p className="f-roles__hint">{t('intro')}</p>
      <div className="f-workdays" role="group" aria-label={t('label')}>
        {WEEKDAYS.map((d) => (
          <Checkbox key={d} checked={selected.has(d)} onChange={(on) => toggle(d, on)} label={t(`day.${d}`)} disabled={!canEdit} />
        ))}
      </div>
      {canEdit ? (
        <div className="f-roles__bar">
          <span className="f-roles__hint">{t('perDay', { count: selected.size })}</span>
          <Button
            size="sm"
            iconLeft={<FontIcon name="floppy-disk" />}
            loading={save.isPending}
            disabled={selected.size === 0}
            onClick={() => save.mutate(WEEKDAYS.filter((d) => selected.has(d)), { onSuccess: () => toast.show(t('saved'), 'success'), onError: (err) => toast.show(errorMessage(err, t('saveError')), 'error') })}
          >
            {t('save')}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
